import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Buttons, FlexContainer, Spinner, Text } from '@taxdown/piccolo-web';
import { locizeNS } from 'Services/i18n';
import { history } from 'Store';
import { routeStateSelector } from 'Store/modules/router/selectors';
import { showModalAction } from 'Store/modules/app/actions';
import { useTranslation } from 'Hooks/useTranslation';
import { useFetchGetPlans } from 'Containers/Plans/hooks';
import { createURL } from 'Containers/Plans/utils';
import FlowColLayout from 'Components/Layouts/FlowColLayout';
import { FRONT_PLANS } from 'Constants/plans';
import { routes } from 'Constants/routes';
import { PAYMENT_METHOD_MODAL } from 'Constants/modals';
import UpsellingProSlider from '../UpsellingProSlider';
import { UpsellingProInfo } from '../UpsellingProInfo';
import * as Styled from './styles';
import { ButtonsMode } from './utils';

const UpsellingPro = (): JSX.Element => {
  const [planType, setPlanType] = useState(FRONT_PLANS.PRO);
  const dispatch = useDispatch();
  const paymentLaterProps = useSelector(routeStateSelector);
  const { data, isLoading } = useFetchGetPlans();
  const { t } = useTranslation(locizeNS.RESULT);

  useEffect(() => {
    if (
      !paymentLaterProps ||
      !paymentLaterProps.taxReturnType ||
      !paymentLaterProps.taxReturnResult
    ) {
      history.push(routes.RESULT);
    }
  }, [paymentLaterProps]);

  useEffect(() => {
    if (paymentLaterProps?.isUserToPro) {
      setPlanType(FRONT_PLANS.FULL);
    }
  }, [paymentLaterProps]);

  const planPro = data?.find(
    plan => plan.name === FRONT_PLANS.PRO || plan.name === FRONT_PLANS.PRO_PLUS
  );
  const planFull = data?.find(
    plan => plan.name === FRONT_PLANS.FULL || plan.name === FRONT_PLANS.FULL_PLUS
  );

  const getSuccessUrl = (): string => {
    if (paymentLaterProps?.fromFedUser) {
      return createURL(routes.CHECKOUT_SUCCESS_STEP, {
        filing: 'true',
        plans: planType,
        fromFedUser: paymentLaterProps?.fromFedUser,
      });
    }
    return createURL(routes.CHECKOUT_SUCCESS_STEP, {
      filing: 'true',
      plans: planType,
    });
  };

  const handleClick = (plan: string): void => {
    const planInfo = plan === FRONT_PLANS.PRO ? planPro : planFull;
    dispatch(
      showModalAction({
        options: {
          type: PAYMENT_METHOD_MODAL,
          data: {
            plan: planType,
            successUrl: getSuccessUrl(),
            cancelUrl: createURL(routes.RESULT),
            upsellingProps: planInfo,
          },
        },
      })
    );
  };

  return (
    <FlowColLayout section="result" isFlow>
      {isLoading ? (
        <FlexContainer fullHeight fullWidth align="center" justify="center">
          <Spinner size="M" align="center" />
        </FlexContainer>
      ) : (
        <Styled.Container
          plan={planType}
          direction="column"
          fullHeight
          fullWidth
          align="center"
          gap="dimension_24"
        >
          <Text type="paragraphs" color="dark" size="XL_XL_S">
            {t(`UpsellingPro.${planType}.title`)}
          </Text>
          {planFull && planPro && (
            <UpsellingProInfo
              planType={planType}
              planPro={planPro}
              setPlanType={setPlanType}
              planFull={planFull}
            />
          )}
          <Styled.ButtonContainer direction="column" fullWidth gap="dimension_24">
            <Buttons.Classic
              label={t(`UpsellingPro.${planType}.button`)}
              mode={ButtonsMode[planType]}
              size="m"
              fullWidth
              onClick={() => handleClick(planType)}
            />
            <UpsellingProSlider planType={planType} />
          </Styled.ButtonContainer>
        </Styled.Container>
      )}
    </FlowColLayout>
  );
};

export default UpsellingPro;
